import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from 'react-redux';
import api, { isCanceled } from "../lib/api";
import useNotify from "../hooks/useNotify";

const ACCOUNT_QUERY = { page: 1, limit: 200, keyword: "", sort_by: "id", sort_order: "DESC" };

const AddNewUser = ({ show, setShow, formData, action, setFormData, setNotification, onSaved }) => {
    const [accounts, setAccounts] = useState({ copart: [], iaai: [] });
    const [loader, setLoader] = useState(false);
    const user = useSelector((state) => state.user);
    const [admins, setAdmins] = useState([]);
    const notify = useNotify(setNotification);

    const isAdd = action === "add";
    const close = () => setShow(false);

    const submit = (e) => {
        e.preventDefault();

        if (formData.role === "") {
            notify.error(null, "Select role to continue");
            return;
        }

        if (isAdd) {
            if (formData.password !== formData.repeat_password) {
                notify.error(null, "Passwords don't match");
                return;
            }
            if ((formData.password || "").length < 6) {
                notify.error(null, "Password must be at least 6 characters");
                return;
            }
        }

        const payload = {
            ...formData,
            copart_accounts: (formData.copart_accounts || []).map((ca) => ca.value),
            iaai_accounts: (formData.iaai_accounts || []).map((ia) => ia.value),
        };

        setLoader(true);
        const request = isAdd
            ? api.post('/users', payload)
            : api.put(`/users/${formData.id}`, payload);

        request
            .then((res) => {
                notify.success(res.data.message.en);
                close();
                if (onSaved) onSaved();
            })
            .catch((err) => notify.error(err))
            .finally(() => setLoader(false));
    };

    useEffect(() => {
        const controller = new AbortController();

        // Both lists load together instead of as two sequential waterfalls.
        Promise.all([
            api.get('/accounts', { params: { ...ACCOUNT_QUERY, auction: "copart" }, signal: controller.signal }),
            api.get('/accounts', { params: { ...ACCOUNT_QUERY, auction: "iaai" }, signal: controller.signal }),
        ])
            .then(([copartRes, iaaiRes]) => {
                const toOptions = (res) => (res.data.data || []).map((account) => ({ label: account.buyer, value: account.buyer }));
                setAccounts({ copart: toOptions(copartRes), iaai: toOptions(iaaiRes) });
            })
            .catch((err) => {
                if (isCanceled(err)) return;
                notify.error(err, "Could not load buyer accounts.");
            });

        api.get('/admins', { signal: controller.signal })
            .then((res) => setAdmins(Array.isArray(res.data.data) ? res.data.data : []))
            .catch((err) => {
                // A moderator is not allowed to list admins; that 403 is
                // expected and the field is hidden for them anyway.
                if (isCanceled(err) || (err.response && err.response.status === 403)) return;
                setAdmins([]);
            });

        return () => controller.abort();
    }, [notify]);

    return (
        <Modal show={show} onHide={close} size="xl" centered>
            <Modal.Header closeButton><h2 className="modal-title-text">{isAdd ? "Add New User" : "Edit User"}</h2></Modal.Header>
            <Modal.Body>
                <div className="container">
                    <form style={{ width: "100%" }} className="row" onSubmit={submit}>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-name">Name*</label>
                                <input id="form-name" required placeholder="Enter dealer name" type="text" className="form-control" name="name" value={formData.name ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-surname">Surname*</label>
                                <input id="form-surname" required placeholder="Enter dealer surname" type="text" className="form-control" name="surname" value={formData.surname ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, surname: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-email">Email*</label>
                                <input id="form-email" required placeholder="Enter email address" type="email" className="form-control" name="email" value={formData.email ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-phone">Phone*</label>
                                <input id="form-phone" required placeholder="Enter phone number" type="text" className="form-control" name="phone" value={formData.phone ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-copart-accounts">Copart Accounts*</label>
                                <Select
                                    inputId="form-copart-accounts"
                                    isMulti
                                    className="no-padding"
                                    classNamePrefix="rs"
                                    options={accounts.copart}
                                    value={formData.copart_accounts ?? []}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, copart_accounts: [...(value || [])] }))}
                                />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-iaai-accounts">IAAI Accounts*</label>
                                <Select
                                    inputId="form-iaai-accounts"
                                    isMulti
                                    className="no-padding"
                                    classNamePrefix="rs"
                                    options={accounts.iaai}
                                    value={formData.iaai_accounts ?? []}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, iaai_accounts: [...(value || [])] }))}
                                />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-amount-limit">Bid Amount Limit*</label>
                                <input id="form-amount-limit" min={0} required placeholder="Enter amount limit" type="number" className="form-control" name="amount-limit" value={formData.limited_amount ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, limited_amount: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-purchase-limit">Purchase Limit*</label>
                                <input id="form-purchase-limit" min={0} required placeholder="Enter purchase limit" type="number" className="form-control" name="purchase-limit" value={formData.purchase_limit ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, purchase_limit: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-role">Role*</label>
                                <select id="form-role" required className="form-control" name="role" value={formData.role ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}>
                                    <option value="">Select Role</option>
                                    <option value="admin">Admin</option>
                                    <option value="moderator">Sub Admin</option>
                                    <option value="user">User</option>
                                </select>
                            </div>
                        </div>
                        {user.role === "admin" && <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-admin">Admin*</label>
                                <select required id="form-admin" className="form-control" name="admin" value={formData.admin ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, admin: e.target.value }))}>
                                    <option value="">Select Admin</option>
                                    {admins.map((admin) => <option key={admin.id} value={admin.id}>{admin.username}</option>)}
                                </select>
                            </div>
                        </div>}
                        <div className="col-md-12">
                            <h4 className="form-section-title">Authorization Info</h4>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-username">Username*</label>
                                <input id="form-username" required placeholder="Enter username" type="text" className="form-control" name="username" value={formData.username ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-password">Password{isAdd && "*"}</label>
                                {/* `?? ""` on both password fields: the edit payload
                                    omits them, and an undefined value silently flips
                                    the input from controlled to uncontrolled. */}
                                <input disabled={!isAdd} id="form-password" required={isAdd} placeholder="Enter password" type="password" className="form-control" name="password" value={formData.password ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group">
                                <label htmlFor="form-repeat-password">Repeat Password{isAdd && "*"}</label>
                                <input disabled={!isAdd} id="form-repeat-password" required={isAdd} placeholder="Repeat Password" type="password" className="form-control" name="repeat_password" value={formData.repeat_password ?? ""} onChange={(e) => setFormData((prev) => ({ ...prev, repeat_password: e.target.value }))} />
                            </div>
                        </div>
                        <div className="col-md-12">
                            <h4 className="form-section-title">Activity Statuses</h4>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group checkbox-container">
                                {/* `checked`, not `defaultChecked`: the modal is not
                                    remounted between rows, so opening Edit for a second
                                    user used to keep showing the first user's toggles. */}
                                <input id="form-copart-status" type="checkbox" name="copart_status" checked={Boolean(formData.copart_status)} onChange={(e) => setFormData((prev) => ({ ...prev, copart_status: e.target.checked }))} />
                                <label htmlFor="form-copart-status">Copart Status</label>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group checkbox-container">
                                <input id="form-iaai-status" type="checkbox" name="iaai_status" checked={Boolean(formData.iaai_status)} onChange={(e) => setFormData((prev) => ({ ...prev, iaai_status: e.target.checked }))} />
                                <label htmlFor="form-iaai-status">IAAI Status</label>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="form-group checkbox-container">
                                <input id="form-is-active" type="checkbox" name="is_active" checked={Boolean(formData.is_active)} onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))} />
                                <label htmlFor="form-is-active">Is Active</label>
                            </div>
                        </div>
                        <div className="modal-action-buttons">
                            <button type="submit" disabled={loader} className="submit-modal-form-btn">{loader ? <div className="button-loader"></div> : (isAdd ? "Submit" : "Save")}</button>
                            {/* Passed an explicit false; it used to hand a boolean to
                                an object-shaped state setter and corrupt `modals`. */}
                            <button type="button" onClick={close} className="cancel-modal-form-btn">Cancel</button>
                        </div>
                    </form>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default AddNewUser
